import axios, { AxiosResponse } from 'axios';
import CryptoJS from 'crypto-js';
import errorHandler from 'Utils/error-handler';
import { setWithExpiry, getWithExpiry } from 'Helpers/getset-with-expiry'
import { reduce, omitBy, isEmpty, isNil, map } from 'lodash';
import normalize from 'Utils/normalizer';

// Function to decrypt loginFile
function decryptLoginFile(loginFile: string, secretPassphrase: string) {
  const base64Words = CryptoJS.enc.Base64.parse(loginFile);
  const encoded = CryptoJS.enc.Utf8.stringify(base64Words).trim();
  const decrypted = CryptoJS.AES.decrypt(encoded, secretPassphrase).toString(CryptoJS.enc.Utf8);

  return JSON.parse(decrypted);
}

interface LoginResponse {
  loginFile: string,
  profile: object,
  roles: Array<any>,
  isVerified?: boolean,
  pid?: number,
  email?: string
}

interface DecryptedLoginFile {
  emailHash: string,
  generatedAt: string,
  loginSalt: string,
  phoneHash: string,
  profileID: string
}

export const authLogin = async ({ email, password }: { email: string; password: string; }) => {
  email = email.toLowerCase();

  const url = `${process.env.API_URL}/${process.env.API_VER}/dem/auth/login`;

  try {
    // DEM login api, no need hashing, just clear text
    const response = await axios.post(url, { email, password });

    let responseData: LoginResponse  = response.data;
    if (responseData.loginFile) {
      const decrypted: DecryptedLoginFile = decryptLoginFile(responseData.loginFile, password);

      const result = reduce((responseData.roles || []), (result: any[], role, index) => {
      
        return result;
      }, [])

      const jwtData = await authJwt({
        email: email,
        loginHash: decrypted.emailHash
      });

      let org: any = {}, selected: any = [];
      const accessToken = jwtData.accessToken

      if (!isEmpty(result) && result.length === 1) {
        selected = result[0];
        org = await authGetOrgDetail(selected.orgID, accessToken);
      }

      // Combine response data from login API and response from JWT request API
      responseData = {
        ...responseData,
        ...jwtData,
        roleTypes: !isEmpty(selected) ? selected.roleTypes : [],
        isRequireRoleTypesCheck: result.length !== 1, // check if org selection is needed
        org
      };
    }
    else if (responseData.isVerified !== undefined) {
      responseData = { ...responseData, email };
    }

    // console.log({ responseData });

    // Store the combined response data in localStorage as authentication passport
    setWithExpiry(process.env.APP_SESSION, responseData);
    return responseData;
  }
  catch(err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, message, stack } = await errorHandler.response(error);
      return { error: { value, code, message, stack }};
    }
    else {
      console.log(err.message)
      return { error: { message: err.message }};
    }
  }
}

export const authBusinessAccountLogin = async ({ email, password }: { email: string; password: string; }) => {
  email = email.toLowerCase();
  const url = `${process.env.API_URL}/${process.env.API_VER}/${process.env.ACCOUNT_TYPE}/auth/business/login`;
  const emailHash = CryptoJS.SHA256(`DEM_BUSINESS:${email}`).toString(CryptoJS.enc.Hex)
  const passwordHash = CryptoJS.SHA256(password).toString(CryptoJS.enc.Hex);

  try {
    const response = await axios.post(url, {
      email: emailHash,
      password: passwordHash
    })

    let responseData: LoginResponse  = response.data;

    if (responseData.loginFile) {

      const decrypted: DecryptedLoginFile = decryptLoginFile(responseData.loginFile, passwordHash);
      const result = reduce((responseData.roles || []), (result: any[], role, index) => {
        if (role.status === 'ACTIVE') {
          const roleTypes = map(role.type, (type: string) => {
            return type === 'HEALTH_PROVIDER' ? 'HEALTHCARE_PROVIDER' : type;
          })

          result[index] = { orgID: role.orgID, roleTypes }
        }

        return result;
      }, [])

      const jwtData = await authJwt({
        email: email,
        loginHash: decrypted.emailHash,
        isBusiness: true,
      });

      // console.log(jwtData)

      let org: any = {}, selected: any = [];
      const accessToken = jwtData.accessToken

      if (!isEmpty(result) && result.length === 1) {
        selected = result[0];
        org = await authGetOrgDetail(selected.orgID, accessToken);
      }

      // Combine response data from login API and response from JWT request API
      responseData = {
        ...responseData,
        ...jwtData,
        roleTypes: !isEmpty(selected) ? selected.roleTypes : [],
        isRequireRoleTypesCheck: result.length !== 1, // check if org selection is needed
        org
      };
    }
    else if (responseData.isVerified !== undefined) {
      responseData = { ...responseData, email };
    }

    // console.log({ responseData });

    // Store the combined response data in localStorage as authentication passport
    setWithExpiry(process.env.APP_SESSION, responseData);

    return responseData;
  }
  catch(err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, type, message, stack } = await errorHandler.response(error);
      return { error: { value, code, type, message, stack }};
    }
    else {
      console.log(err.message)
      return { error: { message: err.message }};
    }
  }
}

export const authRegisterUser = async (data: any) => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/${process.env.ACCOUNT_TYPE}/auth/business/register`;
  const passwordHash = CryptoJS.SHA256(data.password).toString(CryptoJS.enc.Hex);

  // HACKS to remove empty field
  data = omitBy({ ...data }, (value) => value === '');

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: { profile: normalize.model.registration({ ...data }), password: passwordHash },
    });

    return response.data;
  }
  catch(err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, type, message, stack } = await errorHandler.response(error);
      return { error: { value, code, type, message, stack }};
    }
    else {
      console.log(err.message)
      return { error: { message: err.message }};
    }
  }
}

export const authJwt = async ({ email, loginHash, isBusiness }: { email: string; loginHash: string; isBusiness?: boolean }) => {
  email = email.toLowerCase();

  const url = `${process.env.API_URL}/${process.env.API_VER}/dem/auth/jwt`;

  const currentDate = new Date();
  const currentDateInISOString = currentDate.toISOString();
  const mappedAccountType = isBusiness ?
    'DEM_BUSINESS' :
    process.env.ACCOUNT_TYPE
  const emailHash = CryptoJS.SHA256(`${mappedAccountType}:${email}`).toString(CryptoJS.enc.Hex)

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: {
        email: emailHash,
        time: currentDateInISOString,
        loginHash: loginHash,
      }
    })

    return response.data;
  }
  catch(err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, message, stack } = await errorHandler.response(error);
      return { error: { value, code, message, stack }};
    }
    else {
      console.log(err.message)
      return { error: { message: err.message }};
    }
  }
}

export const authRefreshJwt = async () => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/dem/auth/refresh`;
  const session = getWithExpiry(process.env.APP_SESSION);

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: { refreshToken: session.refreshToken }
    })

    let responseData = response.data;
    responseData = { ...session, ...responseData };

    // Replace accessToken and refreshToken
    setWithExpiry(process.env.APP_SESSION, responseData);
    return true;
  }
  catch(err) {
    authRevokeJwtToken();
    if (window) window.location.href = window.location.href; // Force refresh client browser
    return false;
  }
}

export const authForgetPassword = async ({ email }: { email: string; }) => {
  email = email.toLowerCase();

  const url =  `${process.env.CMS_API_URL}/ForgetPassword/${email}`;
  const emailHash = CryptoJS.SHA256(email).toString(CryptoJS.enc.Hex);

  try {
    const response = await axios({
      method: 'GET',
      url: url
    })
    return response;
  }
  catch(err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, message, stack } = await errorHandler.response(error);
      return { error: { value, code, message, stack }};
    }
    else {
      console.log(err.message)
      return { error: { message: err.message }};
    }
  }
}

export const authBusinessAccountForgetPassword = async ({ email }: { email: string; }) => {
  email = email.toLowerCase();

  const url = `${process.env.API_URL}/${process.env.API_VER}/${process.env.ACCOUNT_TYPE}/auth/password/forgot`;
  const emailHash = CryptoJS.SHA256(`DEM_BUSINESS:${email.toLowerCase()}`).toString(CryptoJS.enc.Hex);

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: {
        email: emailHash,
      }
    })

    return response.data;
  }
  catch(err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, type, message, stack } = await errorHandler.response(error);
      return { error: { value, code, type, message, stack }};
    }
    else {
      console.log(err.message)
      return { error: { message: err.message }};
    }
  }
}

export const authResetPassword = async ({ passToken, newPassword }: { passToken: string; newPassword: string; }) => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/${process.env.ACCOUNT_TYPE}/auth/password/reset`;
  const newPasswordHash = CryptoJS.SHA256(newPassword).toString(CryptoJS.enc.Hex);

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: omitBy({
        passToken: passToken,
        newPassword: newPasswordHash,
      }, isNil)
    })

    return response.data;
  }
  catch(err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, type, message, stack } = await errorHandler.response(error);
      return { error: { value, code, type, message, stack }};
    }
    else {
      console.log(err.message)
      return { error: { message: err.message }};
    }
  }
}

export const authChangePassword = async (pid:string, currentPassword: string,
  newPassword:string) => {
  const url =  `${process.env.CMS_API_URL}/ChangePassword/${pid}/${currentPassword}/${newPassword}`;

  try {
    const response = await axios({
      method: 'GET',
      url: url
    })
    return response;
  }
  catch(err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, message, stack } = await errorHandler.response(error);
      return { error: { value, code, message, stack }};
    }
    else {
      console.log(err.message)
      return { error: { message: err.message }};
    }
  }
}

export const authVerifyEmail = async ({ verifyToken, key }: { verifyToken: string, key: string }) => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/${process.env.ACCOUNT_TYPE}/auth/email/verify`;

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: {
        token: verifyToken,
        id: key,
      }
    })

    return response.data.verified;
  }
  catch(err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, type, message, stack } = await errorHandler.response(error);
      return { error: { value, code, type, message, stack }};
    }
    else {
      console.log(err.message)
      return { error: { message: err.message }};
    }
  }
}

export const authBusinessAccountChangePassword = async (currentPassword: string,
  newPassword:string) => {
  const url =  `${process.env.API_URL}/${process.env.API_VER}/${process.env.ACCOUNT_TYPE}/auth/password/update`;
  const session = getWithExpiry(process.env.APP_SESSION);
  const { accessToken } = session;
  const oldPasswordHash = CryptoJS.SHA256(currentPassword).toString(CryptoJS.enc.Hex);
  const newPasswordHash = CryptoJS.SHA256(newPassword).toString(CryptoJS.enc.Hex);

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: { oldPassword: oldPasswordHash, 
              newPassword: newPasswordHash, },
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });
    return response;
  }
  catch(err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, message, stack } = await errorHandler.response(error);
      return { error: { value, code, message, stack }};
    }
    else {
      console.log(err.message)
      return { error: { message: err.message }};
    }
  }
}

export const authResendVerifyEmail = async ({ email }: { email: string; }) => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/${process.env.ACCOUNT_TYPE}/auth/email/verify/resend`;

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: {
        email: email.toLowerCase(),
      }
    })

    return response.data;
  }
  catch(err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, type, message, stack } = await errorHandler.response(error);
      return { error: { value, code, type, message, stack }};
    }
    else {
      console.log(err.message)
      return { error: { message: err.message }};
    }
  }
}

export const authLogout = async () => {
  localStorage.removeItem(process.env.APP_SESSION);
  
}

export const authRevokeJwtToken = () => {
  localStorage.removeItem(process.env.APP_SESSION);
  return true;
}

const authGetOrgDetail = async (id: string, accessToken: string) => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/org/fetch`;

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: { filters: { _id: id } },
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    const responseData = response.data;
    return !isEmpty(responseData.org) ? responseData.org : {};
  }
  catch(err) {
    // return {}
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await authGetOrgDetail(id, accessToken);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack }};
    }
    else {
      console.log(err.message)
      return { error: { message: err.message }};
    }
  }
}

export const emailVerification = async (pid: string, code: string ) => {
  try {
    const response = await axios({
      method: 'GET',
      url: `${process.env.CMS_API_URL}/VerifyRegistrationCode/${pid}/${code}`,
    });
    console.log({ emailVerifyAxios: response });
    if (response) {
      let session = getWithExpiry(process.env.APP_SESSION);
      session.isRegisterVerified = true;
      setWithExpiry(process.env.APP_SESSION, session);
      return session;
    }
    else {
      return false;
    }
  }
  catch(err) {
    console.log(err);
    return false;
  }
}

export const resendEmailVerification = async (pid: string) => {
  try {
    const response = await axios({
      method: 'GET',
      url: `${process.env.CMS_API_URL}/RegistrationVerificationSend/${pid}`,
    });

    if (response) {
      return response;
    }
    else {
      return false;
    }
  }
  catch(err) {
    console.log(err);
    return false;
  }
}

export interface ForgotPasswordBody {
  Email: string,
  PasswordString: string
}

export const forgotPassword = async (data: ForgotPasswordBody) => {
  try {
    const response = await axios({
      method: 'POST',
      url: `${process.env.DRADMIN_API_URL}/ChangeUserPwdHash/`,
      data: data
    });

    /**
     * 0 - Unable to update\
     * 1 - success\
     * 2 - invalid email\
     * 3 - password policy not fullfill (Must Be Alphanumeric, At least One Special Character, At least One UpperCase)
    */
    const result: '0' | '1' | '2' | '3' = response?.ChangeUserPwdHashResult;

    let resData = {};
    if (result === '0' || result === '3') {
      resData = { error: { value: 'error.undefined', type: 'SERVER_ERR' } }
    }
    else if (result === '1') {
      resData = { error: null };
    }
    else if (result === '2') {
      resData = { error: { value: 'error.emailNotFound', type: 'SERVER_ERR' } }
    }

    return resData;
  }
  catch(err) {
    return { error: { value: 'error.undefined', type: 'CLIENT_ERR' } }
  }
}
